import { ReactComponent as Spinner } from "assets/images/spinner-s.svg"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import type { Screen } from "config/types"
import {
  SURVEY_CONFIG_UGC43,
  SURVEY_CONFIG_UGC43_NO_PAYWALL,
} from "config/ugc43"
import { amplitudeClient } from "lib/amplitude"
import { getCompletedStepId, saveCompletedSteps } from "lib/localStorage"
import { Survey } from "../survey"

const SURVEY_ID = "ugc43"
const EXPERIMENT_NAME = "w2a_ugc43_paywall_exp"
const NO_PAYWALL_VARIANT = "no_paywall"
const VARIANT_OVERRIDE_PARAM = "ab_variant"

const fetchExperimentVariant = async () => {
  try {
    await amplitudeClient.fetchExperimentOptions([EXPERIMENT_NAME])
    const variant =
      await amplitudeClient.fetchExperimentVariant(EXPERIMENT_NAME)
    return variant?.value ?? null
  } catch {
    return null
  }
}

const getStepsForVariant = (variant: string | null) =>
  variant === NO_PAYWALL_VARIANT
    ? SURVEY_CONFIG_UGC43_NO_PAYWALL
    : SURVEY_CONFIG_UGC43

const keepSavedStepInside = (steps: Array<Screen[]>) => {
  const lastStepIndex = steps.flat().length - 1
  if (getCompletedStepId(SURVEY_ID) > lastStepIndex) {
    saveCompletedSteps(SURVEY_ID, lastStepIndex)
  }
}

export function Ugc43Gate() {
  const location = useLocation()
  const navigate = useNavigate()
  const [steps, setSteps] = useState<Array<Screen[]> | null>(null)

  useEffect(() => {
    let active = true

    const chooseVariant = async () => {
      const search = new URLSearchParams(location.search)
      const variantOverride = search.get(VARIANT_OVERRIDE_PARAM)
      const variant = variantOverride ?? (await fetchExperimentVariant())

      if (!active) return

      if (variant && !variantOverride) {
        search.set("af_sub4", EXPERIMENT_NAME)
        search.set("af_sub5", variant)
        navigate({ ...location, search: search.toString() }, { replace: true })
      }

      const variantSteps = getStepsForVariant(variant)
      keepSavedStepInside(variantSteps)
      setSteps(variantSteps)
    }

    chooseVariant()

    return () => {
      active = false
    }
  }, [])

  if (!steps) {
    return (
      <div className="max-w-[450px] mx-auto pt-[72px] px-4 pb-10 flex items-center justify-center h-full">
        <Spinner className="animate-spin text-black" />
      </div>
    )
  }

  return <Survey surveyId={SURVEY_ID} steps={steps} />
}
